// src/encounter/referral-add-dialog.tsx
// Add-referral picker for the A/P canvas (PROD-1297). SELF-CONTAINED: takes
// { providerId, onConfirm, onCancel } (+ an optional caller-supplied referral target), runs its
// OWN additional-diagnoses search (useReferralSearch) internally, composes the DS Modal +
// CodeSearchField (additional / other diagnoses) + removable chips + a free-text note TextArea +
// a Confirm button. On confirm it builds { planOrder, payload } via toReferralOrder and hands it
// to onConfirm for the canvas side-map.
//
// ⚠️ CONTRACT-UNCERTAIN — see to-referral-order.ts. v1: the referral TARGET is OPTIONAL and
// caller-supplied (default = a note-only referral); the in-dialog search is for additional
// diagnoses only. The target shape is un-verified pending a live smoke.
//
// orderId is derived from the picked target code (or 'referral') + a passed/incrementing suffix —
// NOT Math.random / Date.now in module scope (order-payload convention).
import { useState } from 'react';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import {
  Modal,
  Button,
  CodeSearchField,
  TextArea,
  type CodeSearchResult,
} from '@scripsteam/scrips-signal-ds';
import { useReferralSearch } from './services/use-referral-search';
import { toReferralOrder, type ReferralOrder, type ReferralTargetInput } from './lib/to-referral-order';
import { AP_DIALOG_LABELS } from './ap-dialog-label-style';
import { FALLBACK_SPECIALITIES, isLegacyDietitian, isSpecialityTargetCode, performerRoleOf, type ReferralSpeciality } from './lib/referral-specialities';
import { ReferralPractitionerPicker } from './referral-practitioner-picker';
import { useOrgSpecialities } from './services/use-org-specialities';
import type { ReferralPractitioner } from './lib/referral-practitioners';

export interface ReferralAddDialogProps {
  open: boolean;
  /** Practitioner id — reserved for provider-scoped search variants; carried for parity with siblings. */
  providerId: string | null | undefined;
  /** Optional referral target (default: a note-only referral). Contract-uncertain — caller-supplied. */
  target?: ReferralTargetInput;
  /** Stable suffix so the generated orderId is deterministic (caller passes an incrementing count). */
  orderIndex?: number;
  saving?: boolean;
  /** Editing an existing order (PROD-1433): pre-fill additional diagnoses + note + "Edit" copy. */
  initialAdditional?: CodeSearchResult[];
  initialNote?: string;
  /** PROD-1395 (`referrals` flag): the practitioner already on the referral being edited (id), or none. */
  initialPractitioner?: ReferralPractitioner | null;
  editing?: boolean;
  onConfirm: (order: ReferralOrder) => void;
  onCancel: () => void;
}

// Was a THIRD uppercase variant (xs / 600 / 0.08em / tertiary) — visually "the same kind of label" as
// the shell's and the med dialog's, which is why the divergence went unnoticed. Now the shared one.
// NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass), so its
// literal values (12/700/uppercase/0.01em/--color-text-hint) are reproduced here as the
// equivalent class string rather than imported as a style object.
const FIELD_LABEL =
  'block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const FIELD_GROUP = 'mb-4';
const CHIP =
  'inline-flex items-center gap-2 py-1 px-2 border border-solid border-border-default rounded-md bg-surface-card text-size-sm text-text-primary';
const CHIP_ROW = 'flex flex-wrap gap-2 mt-2';

/** Stable orderId from the target code (or 'referral') + the caller's incrementing suffix. */
function makeOrderId(target: ReferralTargetInput | undefined, orderIndex: number): string {
  const base = target?.code?.trim() || 'referral';
  return `referral-${base}-${orderIndex}`;
}

/**
 * PROD-3336: a speciality target saved without a name takes the name from the clinic's speciality list. Only then is the
 * list read (by the inner `WithListedSpecialityName`), so the dialog reads nothing extra, and needs no query client, in
 * every other case.
 */
export function ReferralAddDialog(props: ReferralAddDialogProps) {
  // Switch OFF = as before: no clinic-list read, and Save is never blocked by a missing name.
  const [referralsOn] = useState(() => isEncounterFlagOn('referrals'));
  const code = props.target?.code?.trim();
  const needsName =
    referralsOn && props.open && isSpecialityTargetCode(code) && !isLegacyDietitian(code) && !props.target?.name?.trim();
  return needsName ? <WithListedSpecialityName {...props} /> : <ReferralAddDialogBody {...props} />;
}

function WithListedSpecialityName(props: ReferralAddDialogProps) {
  const list = useOrgSpecialities(true);
  const id = props.target?.code?.trim().toLowerCase();
  const listedName = list.data?.find((s) => s.id.toLowerCase() === id)?.name;
  return <ReferralAddDialogBody {...props} listedSpecialityName={listedName} listLoading={list.isFetching} />;
}

interface ReferralAddDialogBodyProps extends ReferralAddDialogProps {
  /** The speciality's name from the clinic list, for a target saved without one. */
  listedSpecialityName?: string;
  listLoading?: boolean;
}

function ReferralAddDialogBody({
  open,
  target,
  orderIndex = 0,
  saving,
  initialAdditional,
  initialNote,
  initialPractitioner,
  editing,
  onConfirm,
  onCancel,
  listedSpecialityName,
  listLoading,
}: ReferralAddDialogBodyProps) {
  // [PROD-3336] `?x=referrals`: "Refer to" is a clinic SPECIALITY with an optional practitioner. Read once per mount.
  // OFF = as before.
  const [referralsOn] = useState(() => isEncounterFlagOn('referrals'));
  // The target's code is the speciality id (a picked or saved speciality), or 'dietitian' (a saved PROD-1395 role referral,
  // or the Dietitian fallback row). No target at all (the canvas "+ Referral" rail) defaults to Dietitian, as before. A
  // loaded legacy target with no code, or with a code that is not a speciality (a procedure code), stays as it was.
  // Switch OFF: an EDIT of a saved speciality or role referral keeps it and its practitioner (no picker is shown), so the save
  // does not strip it and drop the referral from the receiver's card. A new referral with the switch off is as before.
  const code = target?.code?.trim();
  const saved: ReferralSpeciality | undefined = isSpecialityTargetCode(code) && code
    ? { id: code, name: target?.name?.trim() || (isLegacyDietitian(code) ? FALLBACK_SPECIALITIES[0].name : '') }
    : undefined;
  const picked = referralsOn ? (saved ?? (target ? undefined : FALLBACK_SPECIALITIES[0])) : editing ? saved : undefined;
  // A speciality id without a name (a row saved without one) takes the name from the clinic's list (see ReferralAddDialog).
  // If it is still missing, the save is blocked: a referral never saves an empty speciality name.
  const speciality: ReferralSpeciality | undefined = picked ? { ...picked, name: picked.name || listedSpecialityName?.trim() || '' } : undefined;
  // Only with the switch on: OFF keeps today's dialog, whose Save nothing new disables.
  const nameMissing = referralsOn && !!speciality && !speciality.name;
  const resolvingName = nameMissing && !!listLoading;
  const [practitioner, setPractitioner] = useState<ReferralPractitioner | null>(initialPractitioner ?? null);
  const [query, setQuery] = useState('');
  const [additional, setAdditional] = useState<CodeSearchResult[]>(initialAdditional ?? []);
  const [note, setNote] = useState(initialNote ?? '');

  const { data: results, isFetching } = useReferralSearch(query, open);

  if (!open) return null;

  function addDiagnosis(r: CodeSearchResult) {
    setAdditional((prev) => (prev.some((d) => d.id === r.id) ? prev : [...prev, r]));
    setQuery('');
  }

  function removeDiagnosis(id: string) {
    setAdditional((prev) => prev.filter((d) => d.id !== id));
  }

  function handleConfirm() {
    if (nameMissing) return;
    const order = toReferralOrder(
      {
        target,
        additionalDiagnoses: additional,
        note,
        // A dietetics speciality (and the PROD-1395 dietitian) also sends performerRole 'dietitian', for the readers that
        // filter on it; the fallback row has no speciality id to send.
        ...(speciality
          ? {
              practitioner,
              ...(performerRoleOf(speciality) ? { role: { code: performerRoleOf(speciality) as string, name: speciality.name } } : {}),
              ...(isLegacyDietitian(speciality.id) ? {} : { speciality: { id: speciality.id, name: speciality.name } }),
            }
          : {}),
      },
      makeOrderId(target, orderIndex),
    );
    onConfirm(order);
  }

  const targetName = speciality ? speciality.name : target?.name?.trim();

  return (
    // NO MODAL TITLE — consistent with every other order dialog (Andrew, 30 Jul).
    <Modal open onClose={onCancel} size="md">
      <div
        role="group"
        aria-label={editing ? 'Edit referral' : 'Add referral'}
        data-slot="referral-body"
        className={`font-sans ${AP_DIALOG_LABELS}`}
      >
        {/* Referral target — caller-supplied (contract-uncertain); display-only in v1. */}
        <div className={FIELD_GROUP}>
          <span data-slot="referral-field-label" className={FIELD_LABEL}>
            Refer to
          </span>
          <p data-slot="referral-refer-to" className="m-0 text-size-sm text-text-primary">
            {targetName || 'General referral (no specific target)'}
          </p>
        </div>

        {nameMissing && (
          <p role="alert" data-slot="referral-speciality-name-missing" className="m-0 mb-4 text-size-sm text-status-amber-text">
            {resolvingName
              ? 'Loading the speciality name…'
              : 'This speciality has no name in the clinic list, so the referral cannot be saved. Pick the speciality again.'}
          </p>
        )}

        {/* PROD-3336: the optional practitioner of the speciality (flag ON, a speciality target only). */}
        {speciality && !nameMissing && referralsOn && (
          <div className={FIELD_GROUP}>
            <span className={FIELD_LABEL}>Practitioner (optional)</span>
            <ReferralPractitionerPicker speciality={speciality} value={practitioner} onChange={setPractitioner} disabled={saving} />
          </div>
        )}

        {/* Additional / other diagnoses — in-dialog search → removable chips. */}
        <div className={FIELD_GROUP}>
          <span data-slot="referral-field-label" className={FIELD_LABEL}>
            Additional diagnoses
          </span>
          <CodeSearchField
            value={query}
            onChange={setQuery}
            results={results ?? []}
            loading={isFetching}
            placeholder="Search diagnoses…"
            onResultClick={addDiagnosis}
          />
          {additional.length > 0 && (
            <div className={CHIP_ROW} aria-label="Selected additional diagnoses">
              {additional.map((d) => (
                <span key={d.id} data-slot="referral-chip" className={CHIP}>
                  {d.display}
                  {d.code ? (
                    <span className="text-text-hint"> · {d.code}</span>
                  ) : null}
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => removeDiagnosis(d.id)}
                    disabled={saving}
                    aria-label={`Remove ${d.display}`}
                  >
                    ×
                  </Button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Free-text referral note. */}
        <div className={FIELD_GROUP}>
          <TextArea
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for referral…"
            rows={3}
            disabled={saving}
          />
        </div>
      </div>

      <div data-slot="referral-actions" className="flex justify-end gap-2 mt-4">
        <Button variant="tertiary" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleConfirm} disabled={saving || nameMissing}>
          {/* One label across every A/P dialog — Flutter uses S.of(context).save for all of them. */}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}
